import { useState, useEffect } from "react"
import { Check, X, Loader2, FileImage, ShieldCheck } from "lucide-react"
import CoachSidebar from "../components/CoachSidebar"
import EmptyState from "../components/EmptyState"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function CoachVerification() {
  const [verifications, setVerifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  const [openCertId, setOpenCertId] = useState(null)
  const [certImg, setCertImg] = useState(null)
  const [certImgLoading, setCertImgLoading] = useState(false)
  const [actionId, setActionId] = useState(null)
  const [rejectModalId, setRejectModalId] = useState(null)
  const [rejectReason, setRejectReason] = useState("")

  function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem("token")}` }
  }

  const fetchVerifications = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/api/coach/pending-verifications`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to fetch pending verifications")
      setVerifications(await res.json())
      setError("")
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVerifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleCertView = async (id) => {
    if (openCertId === id) {
      setOpenCertId(null)
      setCertImg(null)
      return
    }
    setOpenCertId(id)
    setCertImg(null)
    setCertImgLoading(true)
    try {
      const res = await fetch(`${API}/api/coach/users/${id}/birth-cert`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to load birth certificate")
      const data = await res.json()
      const contentType = data.content_type || "image/png"
      setCertImg({ src: `data:${contentType};base64,${data.birth_certificate_base64}`, isPdf: contentType === "application/pdf" })
    } catch (err) {
      setError(err.message)
    } finally {
      setCertImgLoading(false)
    }
  }

  const handleApprove = async (id) => {
    setActionId(id)
    try {
      const res = await fetch(`${API}/api/coach/verifications/${id}/approve`, {
        method: "POST",
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error("Failed to approve swimmer")
      setVerifications((prev) => prev.filter((v) => v.id !== id))
      if (openCertId === id) { setOpenCertId(null); setCertImg(null) }
    } catch (err) {
      setError(err.message)
    } finally {
      setActionId(null)
    }
  }

  const confirmReject = async (e) => {
    e.preventDefault()
    const id = rejectModalId
    setActionId(id)
    try {
      const res = await fetch(`${API}/api/coach/verifications/${id}/reject`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason }),
      })
      if (!res.ok) throw new Error("Failed to reject swimmer")
      setVerifications((prev) => prev.filter((v) => v.id !== id))
      if (openCertId === id) { setOpenCertId(null); setCertImg(null) }
      setRejectModalId(null)
      setRejectReason("")
    } catch (err) {
      setError(err.message)
    } finally {
      setActionId(null)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <CoachSidebar />
      <main className="flex-1 overflow-y-auto relative">
        <header className="flex items-center justify-between bg-ink-800 px-4 py-6 sm:px-6 lg:px-10 lg:py-8 border-b border-white/10">
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-paper-0">{t("coach_verif_title")}</h1>
            <p className="mt-2 text-sm text-mist-300">{t("coach_verif_subtitle")}</p>
          </div>
        </header>

        <div className="flex-1 p-4 sm:p-6 lg:px-10 lg:py-8">
          {error && (
            <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
          )}

          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gold-500" />
            </div>
          ) : (
            <section>
              <h2 className="mb-4 text-xs font-bold uppercase tracking-[.15em] text-fog-200/80">{t("coach_verif_pending_swimmers")}</h2>
              <div className="flex flex-col gap-4">
                {verifications.map((v) => (
                  <div key={v.id} className="rounded-xl border border-white/10 bg-ink-800/80 p-4 shadow-sm transition-colors hover:bg-white/[0.02]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex h-[48px] w-[48px] flex-shrink-0 items-center justify-center rounded-full bg-gold-500/20 text-lg font-bold text-gold-400 border border-gold-500/30">
                          {v.avatar_initials}
                        </div>
                        <div>
                          <p className="font-medium text-paper-0">{v.full_name}</p>
                          <p className="mt-0.5 text-sm text-mist-300">{v.email} {v.dob ? `· DOB ${v.dob}` : ""}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {v.club_member_status === "pending" && (
                              <span className="rounded bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-400 border border-blue-500/20">{t("coach_verif_mem_pending")}</span>
                            )}
                            {v.birth_cert_status === "pending" && (
                              <span className="rounded bg-purple-500/10 px-2 py-0.5 text-xs font-medium text-purple-400 border border-purple-500/20">{t("coach_verif_cert_pending")}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {v.birth_cert_status === "pending" && (
                          <button
                            onClick={() => toggleCertView(v.id)}
                            className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-paper-0 transition-colors hover:border-white/40"
                          >
                            <FileImage className="h-4 w-4" />
                            {openCertId === v.id ? t("coach_verif_hide") : t("coach_verif_view")}
                          </button>
                        )}
                        <button
                          onClick={() => handleApprove(v.id)}
                          disabled={actionId === v.id}
                          className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400 transition-colors hover:bg-emerald-500 hover:text-ink-900 border border-emerald-500/30 disabled:opacity-50"
                        >
                          <Check className="h-5 w-5" strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={() => { setRejectModalId(v.id); setRejectReason("") }}
                          disabled={actionId === v.id}
                          className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-red-500/10 text-red-400 transition-colors hover:bg-red-500 hover:text-white border border-red-500/30 disabled:opacity-50"
                        >
                          <X className="h-5 w-5" strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>

                    {openCertId === v.id && (
                      <div className="mt-4 border-t border-white/10 pt-4">
                        {certImgLoading ? (
                          <Loader2 className="h-6 w-6 animate-spin text-gold-500" />
                        ) : certImg ? (
                          certImg.isPdf ? (
                            <iframe src={certImg.src} title={`${v.full_name}'s birth certificate`} className="h-[520px] w-full border border-white/10" />
                          ) : (
                            <img src={certImg.src} alt={`${v.full_name}'s birth certificate`} className="max-h-[420px] w-auto border border-white/10" />
                          )
                        ) : (
                          <p className="text-sm text-mist-400">{t("coach_verif_err_load")}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {verifications.length === 0 && (
                  <EmptyState icon={<ShieldCheck className="h-12 w-12 text-mist-600" />} title={t("coach_verif_empty")} />
                )}
              </div>
            </section>
          )}
        </div>
      </main>

      {rejectModalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-ink-800 p-8 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-paper-0">{t("coach_verif_reject_swimmer")}</h2>
              <button onClick={() => setRejectModalId(null)} className="text-mist-400 hover:text-paper-0"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={confirmReject} className="flex flex-col gap-4">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("coach_verif_reject_reason")}</label>
                <textarea
                  required
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder={t("coach_verif_reject_hint")}
                  rows={3}
                  className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none"
                />
              </div>
              <div className="mt-2 flex justify-end gap-3 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setRejectModalId(null)} className="rounded px-4 py-2 text-sm font-semibold text-mist-300 hover:text-paper-0">{t("common_cancel")}</button>
                <button type="submit" disabled={actionId === rejectModalId} className="flex items-center gap-2 rounded bg-red-500 px-6 py-2 text-sm font-bold text-white hover:bg-red-400 disabled:opacity-50">
                  {actionId === rejectModalId ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  {t("coach_verif_confirm_reject")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
